'use client';

import { FC, useState, useEffect, useRef, useCallback } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import Link from 'next/link';
import { DevsuLogoComplete } from '@/assets/DevsuLogoComplete';
import { ArrowRightIcon } from '@/assets/ArrowRightIcon';
import dynamic from 'next/dynamic';
import { calculateVelxImpact } from './calculator';
import { CalculationResult } from './calculator-types';
import { registerContact } from '@/actions/hubspot/registerContact';
import { processCalculatorPdf } from '@/actions/calculator/processCalculatorPdf';
import cookies from 'js-cookie';

// Dynamically import StarfieldBackground
const StarfieldBackground = dynamic(
  () => import('@/components/StarfieldBackground'),
  { ssr: false },
);

// Slider configuration for each input type - keys match Prismic input_id values (1, 2, 3)
const SLIDER_CONFIG: Record<
  string,
  {
    min: number;
    max: number;
    minLabel: string;
    maxLabel: string;
    formatValue: (value: number) => string;
  }
> = {
  '1': {
    min: 2500,
    max: 2000000,
    minLabel: '3K lines',
    maxLabel: '2M+',
    formatValue: (value: number) => {
      if (value >= 1000000) {
        return `${(value / 1000000).toFixed(1)}M lines`;
      }
      if (value >= 1000) {
        return `${(value / 1000).toFixed(0)}K `;
      }
      return `${value.toLocaleString()} lines`;
    },
  },
  '2': {
    min: 1,
    max: 1500,
    minLabel: '1 MB',
    maxLabel: '1,500+ MB',
    formatValue: (value: number) => `${value.toLocaleString()} MB`,
  },
  '3': {
    min: 5,
    max: 500,
    minLabel: '5 - 15',
    maxLabel: '500+',
    formatValue: (value: number) => `${value}`,
  },
};

interface SliderInputProps {
  label: string;
  helperText?: string;
  inputId: string;
  value: number;
  onChange: (value: number) => void;
}

const SliderInput: FC<SliderInputProps> = ({
  label,
  helperText,
  inputId,
  value,
  onChange,
}) => {
  const config = SLIDER_CONFIG[inputId] || SLIDER_CONFIG['1'];
  const percentage = ((value - config.min) / (config.max - config.min)) * 100;
  const displayValue = config.formatValue(value);

  // Fade the min label when slider is near the start (under 15%)
  const minLabelOpacity = percentage < 15 ? 0 : 0.3;
  // Fade the max label when slider is near the end (over 85%)
  const maxLabelOpacity = percentage > 85 ? 0 : 0.3;

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex gap-2 items-center justify-between w-full">
        <p className="font-semibold text-base text-white leading-[1.5]">
          {label} *
        </p>
        {helperText && (
          <p className="text-[10px] text-white whitespace-nowrap">
            💡 {helperText}
          </p>
        )}
      </div>
      <div className="flex flex-col gap-[15px] w-full">
        <div className="flex justify-between text-sm">
          <span
            className="transition-opacity duration-200"
            style={{ color: `rgba(255, 255, 255, ${minLabelOpacity})` }}
          >
            {config.minLabel}
          </span>
          <span
            className="transition-opacity duration-200"
            style={{ color: `rgba(255, 255, 255, ${maxLabelOpacity})` }}
          >
            {config.maxLabel}
          </span>
        </div>
        <div className="relative h-6 w-full">
          {/* Slider track */}
          <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-2 bg-[#002726] rounded" />
          {/* Slider fill */}
          <div
            className="absolute left-0 top-1/2 -translate-y-1/2 h-2 bg-[#05f273] rounded"
            style={{ width: `${percentage}%` }}
          />
          {/* Hidden input */}
          <input
            type="range"
            min={config.min}
            max={config.max}
            value={value}
            onChange={(e) => onChange(Number(e.target.value))}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
          />
          {/* Thumb with value label */}
          <div
            className="absolute top-1/2 -translate-y-1/2 pointer-events-none"
            style={{ left: `calc(${percentage}% - 12px)` }}
          >
            {/* Value label above thumb */}
            <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-white/30 text-sm whitespace-nowrap">
              {displayValue}
            </div>
            {/* Thumb circle */}
            <div className="w-6 h-6 bg-[#05f273] rounded-full" />
          </div>
        </div>
      </div>
    </div>
  );
};

interface CheckboxProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

const Checkbox: FC<CheckboxProps> = ({ label, checked, onChange }) => {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="flex gap-2 items-center py-2 cursor-pointer"
    >
      <div
        className={`w-[22px] h-[22px] rounded-[6px] border flex items-center justify-center shrink-0 ${
          checked
            ? 'bg-[#05f273] border-[#05f273]'
            : 'bg-transparent border-[#616161]'
        }`}
      >
        {checked && (
          <svg
            width="14"
            height="10"
            viewBox="0 0 14 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1 5L5 9L13 1"
              stroke="black"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>
      <span className="font-semibold text-sm text-white leading-[1.5] text-left">
        {label}
      </span>
    </button>
  );
};

// Back arrow icon
const BackArrowIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M15 6L9 12L15 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Format currency
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Results View Component
interface ResultsViewProps {
  result: CalculationResult;
  developerCount: number;
  onBack: () => void;
  onGetReport: () => void;
}

const ResultsView: FC<ResultsViewProps> = ({
  result,
  developerCount,
  onBack,
  onGetReport,
}) => {
  // Monthly rates
  const inHouseMonthlyRate = 15883; // BLS 2024 fully-loaded
  const velxMonthlyRate = 8000; // Velx flat fee

  // Calculate yearly costs (12-month baseline)
  const yearlyTradCost = developerCount * inHouseMonthlyRate * 12;
  const yearlyVelxTotalCost = developerCount * velxMonthlyRate * 12;
  const yearlySavings = yearlyTradCost - yearlyVelxTotalCost;

  // All metrics derived from the cost difference
  // Cost reduction: direct comparison of rates (exact percentage)
  const costReductionPercent = (
    ((inHouseMonthlyRate - velxMonthlyRate) / inHouseMonthlyRate) *
    100
  ).toFixed(1);

  // Time reduction: from project category (based on SLOC and repo size from calculator)
  const timeReductionPercent = result.savings.timeReductionPercent;

  // Efficiency gain: how much more you get for your money
  // With same team, you save on cost AND time, so efficiency = 1 / (cost_ratio * time_ratio)
  const costRatio = velxMonthlyRate / inHouseMonthlyRate; // ~0.504
  const timeRatio = (100 - timeReductionPercent) / 100; // e.g., 0.60 if 40% time reduction
  const efficiencyGain = (1 / (costRatio * timeRatio)).toFixed(2);

  // Handle Get Report - sync calculated values to URL params before scrolling
  const handleGetReportClick = () => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      // Add calculated values to URL params for form submission
      params.set('in_house_cost', String(yearlyTradCost));
      params.set('velx_cost', String(yearlyVelxTotalCost));
      params.set('time_reduction', String(timeReductionPercent));
      params.set('efficiency_gain', efficiencyGain);

      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({}, '', newUrl);
    }
    onGetReport();
  };

  return (
    <div className="flex flex-col gap-6 w-full">
      {/* Header with back button */}
      <div className="flex gap-3 items-center">
        <button
          type="button"
          onClick={onBack}
          className="w-6 h-6 text-white hover:text-[#05f273] transition-colors"
        >
          <BackArrowIcon />
        </button>
        <h2 className="font-semibold text-xl md:text-2xl text-white leading-[1.2]">
          ROI estimated results
        </h2>
      </div>

      {/* Results card - Cost Comparison */}
      <div className="bg-black/80 rounded-[14px] p-4 flex flex-col gap-4">
        {/* Yearly approximation note */}
        <p className="text-xs text-white/50 text-center">
          * Based on 12-month yearly estimates
        </p>

        {/* Current/Traditional Cost */}
        <div className="flex flex-col gap-2 items-center">
          <p className="text-sm text-white/70">
            Current In-House Estimated Cost
          </p>
          <p className="font-medium text-2xl md:text-3xl text-white/60 line-through decoration-white/40">
            {formatCurrency(yearlyTradCost)}
          </p>
          <p className="text-xs text-white/50">
            Fully-loaded cost for {developerCount} developers*
          </p>
          <p className="text-xs text-white/50">
            No Team Structure considered (Managers, QA, etc.)
          </p>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#05f273]/30 to-transparent"></div>
          <span className="text-[#05f273] text-sm font-medium">vs</span>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#05f273]/30 to-transparent"></div>
        </div>

        {/* Velx Cost */}
        <div className="flex flex-col gap-2 items-center">
          <p className="text-sm text-[#05f273]">With Velx-Devsu</p>
          <p className="font-bold text-3xl md:text-4xl text-[#05f273]">
            {formatCurrency(yearlyVelxTotalCost)}
          </p>
          <p className="text-xs text-white/50 text-center max-w-[280px]">
            One flat fee: we handle benefits, taxes, equipment, recruitment and
            more!
          </p>
        </div>

        {/* Savings highlight */}
        <div className="bg-[#05f273]/10 border border-[#05f273]/30 rounded-lg p-3 mt-2">
          <p className="text-center text-[#05f273] font-bold text-lg">
            💰 Save {formatCurrency(yearlySavings)}/year ({costReductionPercent}
            % less)
          </p>
        </div>

        {/* Stats grid */}
        <div className="border-t border-[rgba(5,242,115,0.2)] pt-4 mt-2">
          <p className="font-bold text-sm text-white text-center mb-3">
            Additional Benefits
          </p>
          <div className="grid grid-cols-3 gap-3">
            {/* Cost Reduction */}
            <div className="bg-[rgba(7,42,36,0.5)] rounded-lg p-2 flex flex-col items-center justify-center gap-1">
              <p className="text-[10px] text-white/70">Cost Reduction</p>
              <p className="font-semibold text-lg text-[#05f273]">
                {costReductionPercent}%
              </p>
            </div>

            {/* Time Reduction */}
            <div className="bg-[rgba(7,42,36,0.5)] rounded-lg p-2 flex flex-col items-center justify-center gap-1">
              <p className="text-[10px] text-white/70">Time Reduction</p>
              <p className="font-semibold text-lg text-[#05f273]">
                {timeReductionPercent}%
              </p>
            </div>

            {/* Efficiency Gain */}
            <div className="bg-[rgba(7,42,36,0.5)] rounded-lg p-2 flex flex-col items-center justify-center gap-1">
              <p className="text-[10px] text-white/70">Efficiency</p>
              <p className="font-semibold text-lg text-[#05f273]">
                {efficiencyGain}x
              </p>
            </div>
          </div>
        </div>

        {/* Report prompt */}
        <p className="text-sm text-white text-center mt-2">
          Get more details in a complete PDF report.
        </p>

        {/* Footnote for fully-loaded cost */}
        <p className="text-[10px] text-white/40 text-center mt-1">
          * Based on BLS 2024 median ($131,450) + benefits, taxes, equipment &
          recruitment
        </p>
      </div>

      {/* Disclaimer */}
      <div className="bg-black/80 backdrop-blur-[47.5px] rounded-lg p-1 text-center">
        <p className="text-xs text-white/80">
          💡 Important: These are yearly estimates based on project category and
          team size. For accurate calculations tailored to your context, please
          contact our team.
        </p>
      </div>

      {/* Get Report CTA */}
      <button
        type="button"
        onClick={handleGetReportClick}
        className="w-full bg-white/30 border-t border-white/80 rounded-full px-6 py-3 font-bold text-white text-center hover:bg-white/40 transition-colors"
      >
        Get Your Detailed PDF Report Now
      </button>
    </div>
  );
};

// ── HOMEPAGE VARIATION ────────────────────────────────────────────────────

const HUBSPOT_PORTAL_ID = '7119515';
const HUBSPOT_FORM_ID = 'f27d60b7-aa5b-464f-92b9-9ca51d1890d9';

const CONTACT_FIELD_MAP: Record<string, string> = {
  'full name': 'fullname',
  name: 'firstname',
  'first name': 'firstname',
  'last name': 'lastname',
  company: 'company',
  'email address': 'email',
  email: 'email',
};

const splitFullName = (
  fullName: string,
): { firstName: string; lastName: string } => {
  const parts = fullName.trim().split(/\s+/);
  const firstName = parts[0] || '';
  const lastName = parts.slice(1).join(' ') || '';
  return { firstName, lastName };
};

const HomepageCalculator: FC<{
  slice: Content.AiModernizationRoiCalculatorSliceHomepageVariation;
}> = ({ slice }) => {
  const {
    eyebrow,
    headline,
    subheadline,
    description,
    form_title,
    question_1,
    question_1_options,
    form_section_2,
    calculator_inputs,
    form_contact_info,
    calculator_cta,
  } = slice.primary;

  const sectionRef = useRef<HTMLElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);

  // Compute the initial slider values from the Prismic inputs. Defaults to the
  // per-slider minimums; mirrors the original mount-time initialization.
  const computeInitialSliderValues = (
    inputs: typeof calculator_inputs,
  ): Record<string, number> => {
    const initial: Record<string, number> = {
      '1': SLIDER_CONFIG['1'].min,
      '2': SLIDER_CONFIG['2'].min,
      '3': SLIDER_CONFIG['3'].min,
    };
    const defaultIds = ['1', '2', '3'];
    inputs?.forEach((input, i) => {
      if (input.input_type === 'slider') {
        const id = input.input_id || defaultIds[i] || '1';
        const config = SLIDER_CONFIG[id];
        if (config) {
          initial[id] = config.min;
        }
      }
    });
    return initial;
  };

  // Form state
  const [selectedChallenge, setSelectedChallenge] = useState<string>('');
  const [sliderValues, setSliderValues] = useState<Record<string, number>>(() =>
    computeInitialSliderValues(calculator_inputs),
  );
  const [contactData, setContactData] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Re-init sliders when the `calculator_inputs` reference changes, by adjusting
  // state during render instead of inside an effect.
  const [prevCalculatorInputs, setPrevCalculatorInputs] =
    useState(calculator_inputs);
  if (prevCalculatorInputs !== calculator_inputs) {
    setPrevCalculatorInputs(calculator_inputs);
    setSliderValues(computeInitialSliderValues(calculator_inputs));
  }

  // Fade-in animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const t = entry.target as HTMLElement;
            t.style.opacity = '1';
            t.style.transform = 'translateY(0)';
            observer.unobserve(t);
          }
        });
      },
      { threshold: 0.15 },
    );
    [textRef.current, formRef.current].forEach((el, i) => {
      if (el) {
        el.style.transitionDelay = `${i * 200}ms`;
        observer.observe(el);
      }
    });
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setFieldErrors({});
    setIsSubmitting(true);

    // Validate
    const errors: Record<string, string> = {};
    if (!selectedChallenge) {
      errors['challenge'] = 'Please select a challenge';
    }

    form_contact_info?.forEach((field) => {
      const label = field.field_label || '';
      const val = contactData[label]?.trim();
      if (field.is_required && !val) {
        errors[label] = 'This field is required';
      }
      const key = label.toLowerCase().trim();
      if ((key === 'email address' || key === 'email') && val) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(val)) {
          errors[label] = 'Please enter a valid email';
        }
      }
    });

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setIsSubmitting(false);
      return;
    }

    try {
      // Build HubSpot properties
      const properties: Record<string, string> = {};

      // Map contact info to HubSpot fields
      Object.entries(contactData).forEach(([label, value]) => {
        const key = label.toLowerCase().trim();
        const hsField = CONTACT_FIELD_MAP[key];

        // Handle Full Name — split into firstname + lastname
        if (hsField === 'fullname') {
          const { firstName, lastName } = splitFullName(value);
          properties['firstname'] = firstName;
          properties['lastname'] = lastName;
        } else {
          const fieldName =
            hsField || key.replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
          properties[fieldName] = value;
        }
      });

      // Add calculator data (use velx_calculator_ prefix to match HubSpot fields)
      properties['primary_challenge'] = selectedChallenge;
      properties['velx_calculator_sloc'] = String(
        sliderValues['1'] || SLIDER_CONFIG['1'].min,
      );
      properties['velx_calculator_repository_size__mb_'] = String(
        sliderValues['2'] || SLIDER_CONFIG['2'].min,
      );
      properties['velx_calculator_developer_count'] = String(
        sliderValues['3'] || SLIDER_CONFIG['3'].min,
      );

      // Calculate ROI results and include them
      const calcResult = calculateVelxImpact({
        sloc: sliderValues['1'] || SLIDER_CONFIG['1'].min,
        repoSizeMB: sliderValues['2'] || SLIDER_CONFIG['2'].min,
      });
      properties['velx_calculator_in_house_cost'] = String(
        Math.round(calcResult.traditional.totalCost),
      );
      properties['velx_calculator_velx_cost'] = String(
        Math.round(calcResult.velx.totalCost),
      );
      properties['velx_calculator_time_reduction__'] = String(
        calcResult.savings.timeReductionPercent,
      );
      properties['velx_calculator_efficiency_gains'] = String(
        calcResult.savings.costReductionPercent,
      );

      // Use the Prismic form_id if set, otherwise the hardcoded GUID
      const formId =
        ((slice.primary as Record<string, unknown>).form_id as string) ||
        HUBSPOT_FORM_ID;

      const { postSubmitAction } = await registerContact({
        properties,
        pageUri: typeof window !== 'undefined' ? window.location.href : '',
        pageName: typeof document !== 'undefined' ? document.title : '',
        hubspotPortalIdForm: HUBSPOT_PORTAL_ID,
        hubspotGuidForm: formId,
        hubspotutkCookie: cookies.get('hubspotutk'),
      });

      if (postSubmitAction?.type === 'redirect' && postSubmitAction.value) {
        window.location.href = postSubmitAction.value;
        return;
      }

      setIsSubmitted(true);

      // Fire-and-forget: Generate PDF and upload to HubSpot
      const email = properties['email'] || '';
      const firstName = properties['firstname'] || '';
      const lastName = properties['lastname'] || '';
      const company = properties['company'] || '';

      processCalculatorPdf({
        email,
        firstName,
        lastName,
        company,
        calculatorValues: {
          sloc: String(sliderValues['1'] || SLIDER_CONFIG['1'].min),
          repoSize: String(sliderValues['2'] || SLIDER_CONFIG['2'].min),
          devCount: String(sliderValues['3'] || SLIDER_CONFIG['3'].min),
          inHouseCost: String(Math.round(calcResult.traditional.totalCost)),
          velxCost: String(Math.round(calcResult.velx.totalCost)),
          timeReduction: String(calcResult.savings.timeReductionPercent),
        },
      }).catch((err) => {
        console.error('Background PDF processing failed:', err);
      });
    } catch (err) {
      console.error('Form submission error:', err);
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <section
        data-slice-type="ai_modernization_roi_calculator"
        data-slice-variation="homepageVariation"
        className="relative mx-[30px] overflow-hidden rounded-[24px] bg-[#041118] px-6 py-[60px] md:px-[80px] lg:px-[150px] lg:py-[80px]"
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(180deg, rgba(4,17,24,0.7) 0%, rgba(4,17,24,0.9) 100%)',
          }}
        />
        <div className="relative z-10 flex flex-col items-center justify-center gap-6 py-20 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#00f557]">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <path
                d="M5 13l4 4L19 7"
                stroke="#002726"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h2 className="text-[32px] font-semibold text-white">Thank you!</h2>
          <p className="max-w-md text-[16px] leading-[1.5] text-white/70">
            Your assessment request has been submitted. Our team will get back
            to you shortly with your detailed ROI report.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      data-slice-type="ai_modernization_roi_calculator"
      data-slice-variation="homepageVariation"
      className="relative mx-[30px] overflow-hidden rounded-[24px] bg-[#041118]"
    >
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <StarfieldBackground />
        <div className="absolute inset-0 bg-[rgba(4,17,24,0.7)]" />
      </div>

      <div className="relative z-10 flex flex-col gap-[24px] px-6 pb-[60px] pt-[80px] md:px-[80px] lg:px-[150px] lg:pt-[95px]">
        {/* Eyebrow + line */}
        <div className="flex items-center gap-[60px]">
          {eyebrow && (
            <div className="inline-flex shrink-0 rounded-[89px] border border-[#00f557] px-[16px] py-[8px]">
              <span className="whitespace-nowrap text-[14px] uppercase leading-[1.2] text-white">
                {eyebrow}
              </span>
            </div>
          )}
          <div className="h-px flex-1 bg-gradient-to-r from-[#00f557]/60 to-transparent" />
        </div>

        {/* Two-column layout */}
        <div className="flex flex-col gap-10 lg:flex-row lg:items-start lg:gap-[80px]">
          {/* Left — text content */}
          <div
            ref={textRef}
            className="flex w-full flex-col gap-[24px] text-white lg:w-[478px] lg:shrink-0"
            style={{
              opacity: 0,
              transform: 'translateY(24px)',
              transition: 'opacity 0.6s ease, transform 0.6s ease',
            }}
          >
            {isFilled.richText(headline) && (
              <PrismicRichText
                field={headline}
                components={{
                  heading1: ({ children }) => (
                    <h1 className="text-[28px] font-semibold capitalize leading-[1.15] md:text-[36px] lg:text-[42px]">
                      {children}
                    </h1>
                  ),
                }}
              />
            )}
            <div className="flex flex-col gap-4">
              {isFilled.richText(subheadline) && (
                <PrismicRichText
                  field={subheadline}
                  components={{
                    heading3: ({ children }) => (
                      <h3 className="text-[20px] font-semibold leading-[1.5] text-[#00f557] md:text-[24px]">
                        {children}
                      </h3>
                    ),
                  }}
                />
              )}
              {isFilled.richText(description) && (
                <PrismicRichText
                  field={description}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="text-[16px] font-light leading-[1.5] text-white">
                        {children}
                      </p>
                    ),
                  }}
                />
              )}
            </div>
          </div>

          {/* Right — form card */}
          <div
            ref={formRef}
            className="flex-1"
            style={{
              opacity: 0,
              transform: 'translateY(24px)',
              transition: 'opacity 0.6s ease, transform 0.6s ease',
            }}
          >
            <form
              onSubmit={handleSubmit}
              className="flex flex-col gap-[32px] rounded-[24px] bg-[rgba(0,39,38,0.8)] p-[24px] opacity-90 backdrop-blur-[2px]"
            >
              {/* Form title */}
              {form_title && (
                <h2 className="text-[24px] font-semibold leading-[1.2] text-[#00f557]">
                  {form_title}
                </h2>
              )}

              <div className="flex flex-col gap-[24px]">
                {/* Q1 — Radio buttons */}
                <div className="flex flex-col gap-[16px]">
                  {question_1 && (
                    <p className="text-[16px] font-semibold leading-[1.5] text-white">
                      <span className="mr-1">1.</span> {question_1}
                    </p>
                  )}
                  {fieldErrors['challenge'] && (
                    <p className="text-[12px] text-red-400">
                      {fieldErrors['challenge']}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-x-[24px] gap-y-[8px] pl-[16px]">
                    {question_1_options?.map((opt, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setSelectedChallenge(opt.option || '')}
                        className="flex w-[202px] items-center gap-[8px]"
                      >
                        <div className="flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-full border border-[#666]">
                          {selectedChallenge === opt.option && (
                            <div className="h-[12px] w-[12px] rounded-full bg-[#00f557]" />
                          )}
                        </div>
                        <span className="text-left text-[14px] leading-[1.5] text-white">
                          {opt.option}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Q2 — Sliders */}
                <div className="flex flex-col gap-[8px]">
                  {form_section_2 && (
                    <p className="text-[16px] font-semibold leading-[1.5] text-white">
                      <span className="mr-1">2.</span> {form_section_2}
                    </p>
                  )}
                  <div className="flex flex-col gap-[26px] px-[16px]">
                    {calculator_inputs &&
                      calculator_inputs.length > 0 &&
                      calculator_inputs
                        .filter((input) => input.input_type === 'slider')
                        .map((input, index) => {
                          const defaultIds = ['1', '2', '3'];
                          const inputId =
                            input.input_id || defaultIds[index] || '1';
                          const config =
                            SLIDER_CONFIG[inputId] || SLIDER_CONFIG['1'];
                          return (
                            <SliderInput
                              key={`slider-${index}`}
                              label={input.label || ''}
                              helperText={input.helper_text || undefined}
                              inputId={inputId}
                              value={sliderValues[inputId] ?? config.min}
                              onChange={(v) =>
                                setSliderValues((prev) => ({
                                  ...prev,
                                  [inputId]: v,
                                }))
                              }
                            />
                          );
                        })}
                  </div>
                </div>

                {/* Q3 — Contact info */}
                <div className="flex flex-col">
                  <p className="text-[16px] font-semibold leading-[1.5] text-white">
                    <span className="mr-1">3.</span> Fill your contact
                    information
                  </p>
                  <div className="flex flex-col gap-[16px] px-[16px] pt-[8px]">
                    {form_contact_info?.map((field, i) => {
                      const label = field.field_label || '';
                      const key = label.toLowerCase().trim();
                      const isEmail =
                        key === 'email address' || key === 'email';
                      return (
                        <div key={i} className="flex flex-col">
                          <div className="flex h-[48px] items-center border-b border-white px-[12px] py-[8px]">
                            <input
                              type={isEmail ? 'email' : 'text'}
                              value={contactData[label] || ''}
                              onChange={(e) =>
                                setContactData((prev) => ({
                                  ...prev,
                                  [label]: e.target.value,
                                }))
                              }
                              placeholder={
                                field.placeholder_text ||
                                `${label}${field.is_required ? '*' : ''}`
                              }
                              className="w-full bg-transparent text-[14px] leading-[1.5] text-white outline-none placeholder:text-white/30"
                            />
                          </div>
                          {fieldErrors[label] && (
                            <p className="mt-1 text-[12px] text-red-400">
                              {fieldErrors[label]}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Error message */}
              {submitError && (
                <p className="text-center text-[14px] text-red-400">
                  {submitError}
                </p>
              )}

              {/* Submit button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex w-full items-center justify-center rounded-[100px] bg-[#00f557] px-[24px] py-[12px] text-[16px] font-bold leading-[1.5] text-[#002726] transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {isSubmitting
                  ? 'Submitting...'
                  : calculator_cta?.text || 'Get Free Assessment'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// ── VELX AUDIT FORM VARIATION ─────────────────────────────────────────────

const VelxAuditForm: FC<{
  slice: Content.AiModernizationRoiCalculatorSliceVelxAuditForm;
}> = ({ slice }) => {
  const {
    eyebrow,
    headline,
    description,
    bottleneck_section_label,
    form_section_label,
    terms_label,
    terms_link,
    submit_cta,
    form_id,
    bottleneck_cards,
    form_contact_info,
    bg_image,
  } = slice.primary;

  const [selectedCardIndex, setSelectedCardIndex] = useState<number | null>(
    null,
  );
  const [contactData, setContactData] = useState<Record<string, string>>({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Project specification sliders: '1' = SLOC, '2' = Repo size (MB),
  // '3' = Internal team size. These drive the ROI metrics sent to HubSpot
  // and the generated PDF (the audit form owns these inputs directly).
  const [sliderValues, setSliderValues] = useState<Record<string, number>>({
    '1': SLIDER_CONFIG['1'].min,
    '2': SLIDER_CONFIG['2'].min,
    '3': SLIDER_CONFIG['3'].min,
  });
  // Project complexity factors: increase the traditional effort estimate
  // (+20% / +20% / +10%) and feed the HubSpot "multiplier" property.
  const [projectFactors, setProjectFactors] = useState({
    legacyTech: false,
    poorDocumentation: false,
    distributedTeams: false,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setFieldErrors({});
    setIsSubmitting(true);

    const errors: Record<string, string> = {};
    if (selectedCardIndex === null) {
      errors['card'] = 'Please select a bottleneck';
    }
    if (!termsAccepted) {
      errors['terms'] = 'Please accept the terms';
    }

    form_contact_info?.forEach((field) => {
      const label = field.field_label || '';
      const val = contactData[label]?.trim();
      if (field.is_required && !val) {
        errors[label] = 'This field is required';
      }
      const key = label.toLowerCase().trim();
      if ((key === 'email address' || key === 'email') && val) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(val)) {
          errors[label] = 'Please enter a valid email';
        }
      }
    });

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setIsSubmitting(false);
      return;
    }

    try {
      const properties: Record<string, string> = {};

      Object.entries(contactData).forEach(([label, value]) => {
        const key = label.toLowerCase().trim();
        const hsField = CONTACT_FIELD_MAP[key];
        if (hsField === 'fullname') {
          const { firstName, lastName } = splitFullName(value);
          properties['firstname'] = firstName;
          properties['lastname'] = lastName;
        } else {
          const fieldName =
            hsField || key.replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
          properties[fieldName] = value;
        }
      });

      const selectedCard =
        selectedCardIndex !== null ? bottleneck_cards[selectedCardIndex] : null;
      if (selectedCard?.card_title) {
        properties['primary_challenge'] = selectedCard.card_title;
      }

      // Compute ROI metrics from the audit form's own sliders and send the
      // same values to HubSpot that go into the generated PDF.
      const sloc = sliderValues['1'] || SLIDER_CONFIG['1'].min;
      const repoSizeMB = sliderValues['2'] || SLIDER_CONFIG['2'].min;
      const devCount = sliderValues['3'] || SLIDER_CONFIG['3'].min;

      const calcResult = calculateVelxImpact({
        sloc,
        repoSizeMB,
        hasLegacyTech: projectFactors.legacyTech,
        hasPoorDocumentation: projectFactors.poorDocumentation,
        hasDistributedTeams: projectFactors.distributedTeams,
      });

      const inHouseCost = String(Math.round(calcResult.traditional.totalCost));
      const velxCost = String(Math.round(calcResult.velx.totalCost));
      const timeReduction = String(calcResult.savings.timeReductionPercent);

      // Semicolon-separated for the HubSpot multi-checkbox property.
      // Labels match the hero_with_calculator_widget / LeadMagnetHero flow.
      const multiplierValues: string[] = [];
      if (projectFactors.legacyTech) {
        multiplierValues.push('Legacy Tech');
      }
      if (projectFactors.poorDocumentation) {
        multiplierValues.push('Low Documentation');
      }
      if (projectFactors.distributedTeams) {
        multiplierValues.push('Distributed Teams');
      }

      properties['velx_calculator_sloc'] = String(sloc);
      properties['velx_calculator_repository_size__mb_'] = String(repoSizeMB);
      properties['velx_calculator_developer_count'] = String(devCount);
      properties['velx_calculator_in_house_cost'] = inHouseCost;
      properties['velx_calculator_velx_cost'] = velxCost;
      properties['velx_calculator_time_reduction__'] = timeReduction;
      properties['velx_calculator_efficiency_gains'] = String(
        calcResult.savings.costReductionPercent,
      );
      if (multiplierValues.length > 0) {
        properties['multiplier'] = multiplierValues.join(';');
      }

      const formId = form_id || HUBSPOT_FORM_ID;

      const { postSubmitAction } = await registerContact({
        properties,
        pageUri: typeof window !== 'undefined' ? window.location.href : '',
        pageName: typeof document !== 'undefined' ? document.title : '',
        hubspotPortalIdForm: HUBSPOT_PORTAL_ID,
        hubspotGuidForm: formId,
        hubspotutkCookie: cookies.get('hubspotutk'),
      });

      if (postSubmitAction?.type === 'redirect' && postSubmitAction.value) {
        window.location.href = postSubmitAction.value;
        return;
      }

      setIsSubmitted(true);

      // Fire-and-forget: generate the PDF and upload it to the HubSpot
      // contact. Runs in the background so it never blocks the user.
      const email = properties['email'] || '';
      const firstName = properties['firstname'] || '';
      const lastName = properties['lastname'] || '';
      const company = properties['company'] || '';

      processCalculatorPdf({
        email,
        firstName,
        lastName,
        company,
        calculatorValues: {
          sloc: String(sloc),
          repoSize: String(repoSizeMB),
          devCount: String(devCount),
          inHouseCost,
          velxCost,
          timeReduction,
        },
      }).catch((err) => {
        console.error('Background PDF processing failed:', err);
      });
    } catch (err) {
      console.error('Form submission error:', err);
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <section
        data-slice-type="ai_modernization_roi_calculator"
        data-slice-variation="velxAuditForm"
        className="relative mx-[30px] overflow-hidden rounded-[24px] bg-[#041118] px-6 py-[60px] md:px-[80px] lg:px-[150px] lg:py-[80px]"
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(180deg, rgba(4,17,24,0.7) 0%, rgba(4,17,24,0.9) 100%)',
          }}
        />
        <div className="relative z-10 flex flex-col items-center justify-center gap-6 py-20 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#00f557]">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <path
                d="M5 13l4 4L19 7"
                stroke="#002726"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h2 className="text-[32px] font-semibold text-white">Thank you!</h2>
          <p className="max-w-md text-[16px] leading-[1.5] text-white/70">
            Your audit request has been submitted. Our team will get back to you
            shortly.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      data-slice-type="ai_modernization_roi_calculator"
      data-slice-variation="velxAuditForm"
      className="relative mx-[30px] overflow-hidden rounded-[24px] bg-[#041118]"
    >
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        {isFilled.image(bg_image) ? (
          <PrismicNextImage
            field={bg_image}
            fill
            className="object-cover"
            fallbackAlt=""
          />
        ) : (
          <StarfieldBackground />
        )}
        <div className="absolute inset-0 bg-[rgba(4,17,24,0.7)]" />
      </div>

      <div className="relative z-10 flex flex-col gap-[32px] px-6 pb-[60px] pt-[80px] md:px-[80px] lg:px-[150px] lg:pt-[95px]">
        {/* Eyebrow + line */}
        <div className="flex items-center gap-[60px]">
          {eyebrow && (
            <div className="inline-flex shrink-0 rounded-[89px] border border-[#00f557] px-[16px] py-[8px]">
              <span className="whitespace-nowrap text-[14px] uppercase leading-[1.2] text-white">
                {eyebrow}
              </span>
            </div>
          )}
          <div className="h-px flex-1 bg-gradient-to-r from-[#00f557]/60 to-transparent" />
        </div>

        {/* Headline + description */}
        <div className="flex flex-col gap-3">
          {isFilled.richText(headline) && (
            <PrismicRichText
              field={headline}
              components={{
                heading1: ({ children }) => (
                  <h1 className="text-[28px] font-semibold leading-[1.15] text-white md:text-[36px] lg:text-[42px]">
                    {children}
                  </h1>
                ),
              }}
            />
          )}
          {isFilled.richText(description) && (
            <PrismicRichText
              field={description}
              components={{
                paragraph: ({ children }) => (
                  <p className="text-[16px] font-light leading-[1.5] text-white/70">
                    {children}
                  </p>
                ),
              }}
            />
          )}
        </div>

        {/* Form card */}
        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-[32px] rounded-[24px] bg-[rgba(0,39,38,0.8)] p-[24px] backdrop-blur-[2px] md:p-[40px]"
        >
          {/* Bottleneck card selection */}
          <div className="flex flex-col gap-[16px]">
            {bottleneck_section_label && (
              <p className="text-[18px] font-semibold leading-[1.5] text-[#00f557]">
                {bottleneck_section_label}
              </p>
            )}
            {fieldErrors['card'] && (
              <p className="text-[12px] text-red-400">{fieldErrors['card']}</p>
            )}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {bottleneck_cards?.map((card, i) => {
                const isSelected = selectedCardIndex === i;
                return (
                  <div
                    key={i}
                    onClick={() => setSelectedCardIndex(i)}
                    className={`flex cursor-pointer flex-col gap-3 rounded-[24px] border p-6 backdrop-blur-[21px] transition-all duration-200 ${
                      isSelected
                        ? 'border-[#00f557]'
                        : 'border-white/20 hover:border-white/40'
                    }`}
                    style={{
                      backgroundImage:
                        'linear-gradient(180deg, rgb(4,17,24) 46.68%, rgba(0,0,0,0) 100%), linear-gradient(270deg, rgb(4,17,24) 0%, rgb(19,9,74) 50%, rgb(4,17,24) 100%)',
                    }}
                  >
                    <p className="text-[16px] font-semibold leading-[1.3] text-white">
                      {card.card_title}
                    </p>
                    {card.card_description && (
                      <p className="flex-1 text-[13px] leading-[1.5] text-white/60">
                        {card.card_description}
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCardIndex(i);
                      }}
                      className={`group mt-auto flex w-full items-center justify-center rounded-full px-4 py-2 text-[13px] font-semibold transition-all duration-300 from-[#00F2A5] to-[#00C2FF] hover:bg-gradient-to-r hover:shadow-[0_0_15px_rgba(0,242,165,0.8)] hover:text-white ${
                        isSelected
                          ? 'bg-[#00f557] text-[#002726]'
                          : 'border border-white/60 bg-transparent text-white hover:border-transparent'
                      }`}
                    >
                      <ArrowRightIcon className="mr-0 w-0 opacity-0 transition-all duration-300 group-hover:mr-2 group-hover:w-4 group-hover:opacity-100" />
                      {card.card_button_label || 'Select'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Project specifications (drives ROI metrics + PDF) */}
          <div className="flex flex-col gap-[20px]">
            <p className="text-[18px] font-semibold leading-[1.5] text-[#00f557]">
              Project specifications
            </p>
            <div className="flex flex-col gap-[26px] px-[4px] sm:px-[16px]">
              <SliderInput
                label="Source lines of code (SLOC)"
                helperText="Typical: 2K - 2M+ lines"
                inputId="1"
                value={sliderValues['1'] ?? SLIDER_CONFIG['1'].min}
                onChange={(v) =>
                  setSliderValues((prev) => ({ ...prev, '1': v }))
                }
              />
              <SliderInput
                label="Repository size (MB)"
                helperText="Typical: 1 - 1500+ MB"
                inputId="2"
                value={sliderValues['2'] ?? SLIDER_CONFIG['2'].min}
                onChange={(v) =>
                  setSliderValues((prev) => ({ ...prev, '2': v }))
                }
              />
              <SliderInput
                label="Internal team size"
                helperText="Typical: 5 - 500+"
                inputId="3"
                value={sliderValues['3'] ?? SLIDER_CONFIG['3'].min}
                onChange={(v) =>
                  setSliderValues((prev) => ({ ...prev, '3': v }))
                }
              />
            </div>
            <div className="flex flex-col gap-[8px] px-[4px] sm:px-[16px]">
              <p className="text-[14px] leading-[1.5] text-white/70">
                Select the factors that apply to your project challenges:
              </p>
              <div className="flex flex-col gap-1 sm:flex-row sm:flex-wrap sm:gap-x-8">
                <Checkbox
                  label="Legacy technology"
                  checked={projectFactors.legacyTech}
                  onChange={(checked) =>
                    setProjectFactors((prev) => ({
                      ...prev,
                      legacyTech: checked,
                    }))
                  }
                />
                <Checkbox
                  label="Poor documentation"
                  checked={projectFactors.poorDocumentation}
                  onChange={(checked) =>
                    setProjectFactors((prev) => ({
                      ...prev,
                      poorDocumentation: checked,
                    }))
                  }
                />
                <Checkbox
                  label="Distributed teams"
                  checked={projectFactors.distributedTeams}
                  onChange={(checked) =>
                    setProjectFactors((prev) => ({
                      ...prev,
                      distributedTeams: checked,
                    }))
                  }
                />
              </div>
            </div>
          </div>

          {/* Contact info */}
          <div className="flex flex-col gap-[16px]">
            {form_section_label && (
              <p className="text-[16px] font-semibold leading-[1.5] text-white">
                {form_section_label}
              </p>
            )}
            <div className="grid grid-cols-1 gap-x-6 sm:grid-cols-2">
              {form_contact_info?.map((field, i) => {
                const label = field.field_label || '';
                const key = label.toLowerCase().trim();
                const isEmail = key === 'email address' || key === 'email';
                return (
                  <div key={i} className="flex flex-col">
                    <div className="flex h-[48px] items-center border-b border-white px-[12px] py-[8px]">
                      <input
                        type={isEmail ? 'email' : 'text'}
                        value={contactData[label] || ''}
                        onChange={(e) =>
                          setContactData((prev) => ({
                            ...prev,
                            [label]: e.target.value,
                          }))
                        }
                        placeholder={
                          field.placeholder_text ||
                          `${label}${field.is_required ? ' *' : ''}`
                        }
                        className="w-full bg-transparent text-[14px] leading-[1.5] text-white outline-none placeholder:text-white/30"
                      />
                    </div>
                    {fieldErrors[label] && (
                      <p className="mt-1 text-[12px] text-red-400">
                        {fieldErrors[label]}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Terms + submit */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setTermsAccepted(!termsAccepted)}
                  className="shrink-0"
                >
                  <div
                    className={`flex h-[22px] w-[22px] items-center justify-center rounded-[6px] border ${
                      termsAccepted
                        ? 'border-[#05f273] bg-[#05f273]'
                        : 'border-[#616161] bg-transparent'
                    }`}
                  >
                    {termsAccepted && (
                      <svg
                        width="14"
                        height="10"
                        viewBox="0 0 14 10"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1 5L5 9L13 1"
                          stroke="black"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                </button>
                <span className="text-[13px] leading-[1.5] text-white/80">
                  {terms_label || 'I accept the'}{' '}
                  {isFilled.link(terms_link) ? (
                    <PrismicNextLink
                      field={terms_link}
                      className="text-[#00f557] underline hover:opacity-80 transition-opacity"
                    >
                      {terms_link.text || 'Terms and conditions'}
                    </PrismicNextLink>
                  ) : (
                    'Terms and conditions'
                  )}
                  .
                </span>
              </div>
              {fieldErrors['terms'] && (
                <p className="text-[12px] text-red-400">
                  {fieldErrors['terms']}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="group flex shrink-0 items-center justify-center rounded-[100px] bg-[#00f557] px-[24px] py-[12px] text-[16px] font-bold leading-[1.5] text-[#002726] transition-all duration-300 from-[#00F2A5] to-[#00C2FF] hover:bg-gradient-to-r hover:shadow-[0_0_15px_rgba(0,242,165,0.8)] hover:text-white disabled:opacity-50"
            >
              <ArrowRightIcon className="mr-0 w-0 opacity-0 transition-all duration-300 group-hover:mr-2 group-hover:w-5 group-hover:opacity-100" />
              {isSubmitting
                ? 'Submitting...'
                : submit_cta?.text || 'Get Audit Estimation'}
            </button>
          </div>

          {submitError && (
            <p className="text-center text-[14px] text-red-400">
              {submitError}
            </p>
          )}
        </form>
      </div>
    </section>
  );
};

/**
 * Props for `AiModernizationRoiCalculator`.
 */
export type AiModernizationRoiCalculatorProps =
  SliceComponentProps<Content.AiModernizationRoiCalculatorSlice>;

/**
 * Component for "AiModernizationRoiCalculator" Slices.
 */
const AiModernizationRoiCalculator: FC<AiModernizationRoiCalculatorProps> = ({
  slice,
}) => {
  if (slice.variation === 'homepageVariation') {
    return <HomepageCalculator slice={slice} />;
  }

  if (slice.variation === 'velxAuditForm') {
    return <VelxAuditForm slice={slice} />;
  }

  return <DefaultCalculator slice={slice} />;
};

const DefaultCalculator: FC<{
  slice: Content.AiModernizationRoiCalculatorSliceHeroWithCalculatorWidget;
}> = ({ slice }) => {
  const {
    logo,
    headline,
    subheadline,
    description,
    primary_cta,
    secondary_cta,
    calculator_inputs,
    project_factors,
    calculator_cta,
  } = slice.primary;

  // Animation refs
  const textRef = useRef<HTMLDivElement>(null);
  const calculatorRef = useRef<HTMLDivElement>(null);

  // Compute initial slider/checkbox state from the Prismic props. These mirror
  // the original mount-time initialization and read only props (SSR-safe).
  const computeInitialSliderValues = (
    inputs: typeof calculator_inputs,
  ): Record<string, number> => {
    const initialValues: Record<string, number> = {
      '1': SLIDER_CONFIG['1'].min,
      '2': SLIDER_CONFIG['2'].min,
      '3': SLIDER_CONFIG['3'].min,
    };
    inputs?.forEach((input) => {
      if (input.input_type === 'slider' && input.input_id) {
        const config = SLIDER_CONFIG[input.input_id];
        if (config) {
          initialValues[input.input_id] = config.min;
        }
      }
    });
    return initialValues;
  };

  const computeInitialCheckedFactors = (
    factors: typeof project_factors,
  ): Record<string, boolean> => {
    const initialChecked: Record<string, boolean> = {};
    factors?.forEach((factor, index) => {
      initialChecked[`factor_${index}`] = factor.default_checked || false;
    });
    return initialChecked;
  };

  // Initialize slider values and checkbox factors from props
  const [sliderValues, setSliderValues] = useState<Record<string, number>>(() =>
    computeInitialSliderValues(calculator_inputs),
  );
  const [checkedFactors, setCheckedFactors] = useState<Record<string, boolean>>(
    () => computeInitialCheckedFactors(project_factors),
  );

  // State for showing results
  const [showResults, setShowResults] = useState(false);
  const [calculationResult, setCalculationResult] =
    useState<CalculationResult | null>(null);

  // Sync slider values and checkbox factors to URL params in real-time
  const syncToUrlParams = useCallback(
    (
      newSliderValues: Record<string, number>,
      newCheckedFactors?: Record<string, boolean>,
    ) => {
      if (typeof window === 'undefined') {
        return;
      }

      const params = new URLSearchParams(window.location.search);
      params.set(
        'sloc',
        String(newSliderValues['1'] || SLIDER_CONFIG['1'].min),
      );
      params.set(
        'repo_size',
        String(newSliderValues['2'] || SLIDER_CONFIG['2'].min),
      );
      params.set(
        'dev_count',
        String(newSliderValues['3'] || SLIDER_CONFIG['3'].min),
      );

      // Sync checkbox multipliers if provided
      // Format: semicolon-separated values for HubSpot multiple checkboxes
      const factors = newCheckedFactors || checkedFactors;
      const multiplierValues: string[] = [];
      if (factors['factor_0']) {
        multiplierValues.push('Legacy Tech');
      }
      if (factors['factor_1']) {
        multiplierValues.push('Low Documentation');
      }
      if (factors['factor_2']) {
        multiplierValues.push('Distributed Teams');
      }
      params.set('multiplier', multiplierValues.join(';'));

      // Update URL without reloading
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({}, '', newUrl);
    },
    [checkedFactors],
  );

  // Calculate ROI handler
  const handleCalculateROI = () => {
    try {
      const result = calculateVelxImpact({
        sloc: sliderValues['1'] || SLIDER_CONFIG['1'].min,
        repoSizeMB: sliderValues['2'] || SLIDER_CONFIG['2'].min,
        // Map checkbox factors to multipliers
        // factor_0 = Legacy Technology, factor_1 = Poor Documentation, factor_2 = Distributed Teams
        hasLegacyTech: checkedFactors['factor_0'] || false,
        hasPoorDocumentation: checkedFactors['factor_1'] || false,
        hasDistributedTeams: checkedFactors['factor_2'] || false,
      });
      setCalculationResult(result);
      setShowResults(true);
    } catch (error) {
      console.error('Calculation error:', error);
    }
  };

  // Back to calculator handler
  const handleBackToCalculator = () => {
    setShowResults(false);
  };

  // Get report handler - scrolls to the form section at the bottom
  // Note: URL params are already synced via updateSliderValue
  const handleGetReport = () => {
    // Find the LeadMagnetHero form section (next slice with a form)
    const formSection = document.querySelector(
      '[data-slice-type="lead_magnet_hero"]',
    );
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      // Fallback: scroll to bottom of the page
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  // Re-initialize slider/checkbox state when the source props change, by
  // adjusting state during render instead of inside an effect.
  const [prevCalculatorInputs, setPrevCalculatorInputs] =
    useState(calculator_inputs);
  const [prevProjectFactors, setPrevProjectFactors] = useState(project_factors);
  if (
    prevCalculatorInputs !== calculator_inputs ||
    prevProjectFactors !== project_factors
  ) {
    setPrevCalculatorInputs(calculator_inputs);
    setPrevProjectFactors(project_factors);
    setSliderValues(computeInitialSliderValues(calculator_inputs));
    setCheckedFactors(computeInitialCheckedFactors(project_factors));
  }

  // Sync the initial (and re-initialized) values to the URL params. This is a
  // real external-system side effect, so it stays in an effect (no setState).
  useEffect(() => {
    syncToUrlParams(
      computeInitialSliderValues(calculator_inputs),
      computeInitialCheckedFactors(project_factors),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculator_inputs, project_factors]);

  // Animation effect
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('opacity-0');
            entry.target.classList.add('animate-slide-in-from-bottom');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 },
    );

    const elements = [textRef.current, calculatorRef.current];
    elements.forEach((el, index) => {
      if (el) {
        el.style.animationDelay = `${index * 200}ms`;
        observer.observe(el);
      }
    });

    return () => observer.disconnect();
  }, []);

  const updateSliderValue = (inputId: string, value: number) => {
    const newValues = { ...sliderValues, [inputId]: value };
    setSliderValues(newValues);
    // Sync to URL immediately
    syncToUrlParams(newValues);
  };

  const updateCheckedFactor = (factorId: string, checked: boolean) => {
    const newFactors = { ...checkedFactors, [factorId]: checked };
    setCheckedFactors(newFactors);
    // Sync to URL immediately
    syncToUrlParams(sliderValues, newFactors);
  };

  return (
    <section
      data-slice-type="ai_modernization_roi_calculator"
      data-slice-variation="hero_with_calculator_widget"
      className="relative w-full min-h-screen overflow-hidden"
    >
      {/* Background gradient */}
      <div
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{
          background: 'linear-gradient(180deg, #0E1010 0%, #0F423A 100%)',
          zIndex: 0,
        }}
      />

      {/* Starfield background */}
      <div className="absolute inset-0 z-[1]">
        <StarfieldBackground />
      </div>

      {/* Content */}
      <div className="relative z-10 px-4 md:px-8 lg:px-[150px] py-16 lg:py-[100px] min-h-screen flex items-center justify-center">
        <div className="w-full max-w-[1140px] flex flex-col lg:flex-row gap-10 lg:gap-[60px] items-center">
          {/* Left side - Text content */}
          <div
            ref={textRef}
            className="flex flex-col gap-10 lg:gap-[60px] items-start justify-center w-full lg:w-[610px] lg:flex-shrink-0"
          >
            {/* Logo - links to homepage */}
            <Link href="/" aria-label="Devsu home page">
              {isFilled.image(logo) ? (
                <PrismicNextImage field={logo} className="h-8 w-auto" />
              ) : (
                <DevsuLogoComplete />
              )}
            </Link>

            {/* Text content */}
            <div className="flex flex-col gap-6 items-start w-full">
              {/* Headline */}
              <PrismicRichText
                field={headline}
                components={{
                  heading1: ({ children }) => (
                    <h1 className="font-extrabold text-[32px] md:text-[40px] lg:text-[48px] xl:text-[52px] leading-[1.1] text-white">
                      {children}
                    </h1>
                  ),
                }}
              />

              {/* Subheadline and description */}
              <div className="flex flex-col gap-4">
                <PrismicRichText
                  field={subheadline}
                  components={{
                    heading3: ({ children }) => (
                      <h3 className="font-semibold text-xl md:text-2xl lg:text-[28px] xl:text-[32px] leading-[1.2] text-[#05f273]">
                        {children}
                      </h3>
                    ),
                  }}
                />
                <PrismicRichText
                  field={description}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="text-base md:text-lg lg:text-xl text-white leading-[1.5]">
                        {children}
                      </p>
                    ),
                  }}
                />
              </div>

              {/* CTA buttons */}
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 items-start">
                {isFilled.link(primary_cta) && (
                  <PrismicNextLink
                    field={primary_cta}
                    className="secondary-button flex items-center gap-2"
                  >
                    {primary_cta.text || 'Explore Devsu Services'}
                    <ArrowRightIcon className="w-5 h-5" />
                  </PrismicNextLink>
                )}
                {isFilled.link(secondary_cta) && (
                  <PrismicNextLink
                    field={secondary_cta}
                    className="border border-white/80 rounded-full px-6 py-3 font-bold text-white hover:bg-white/10 transition-colors"
                  >
                    {secondary_cta.text || 'View Case Studies'}
                  </PrismicNextLink>
                )}
              </div>
            </div>
          </div>

          {/* Right side - Calculator widget */}
          <div
            ref={calculatorRef}
            className="flex flex-col gap-3 items-center w-full lg:w-[470px] lg:flex-shrink-0"
          >
            {/* Calculator card */}
            <div
              className={`bg-black/40 backdrop-blur-sm rounded-2xl p-6 w-full opacity-90 ${
                showResults
                  ? 'shadow-[0px_0px_20px_0px_rgba(5,242,115,0.3)]'
                  : ''
              }`}
            >
              {showResults && calculationResult ? (
                <ResultsView
                  result={calculationResult}
                  developerCount={sliderValues['3'] || SLIDER_CONFIG['3'].min}
                  onBack={handleBackToCalculator}
                  onGetReport={handleGetReport}
                />
              ) : (
                <div className="flex flex-col gap-6 w-full">
                  {/* Header */}
                  <div className="flex gap-3 items-center">
                    <div className="w-9 h-9 bg-[#0F423A] rounded-lg flex items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                      >
                        <path
                          d="M17 17L13.1396 13.1396M13.1396 13.1396C13.7999 12.4793 14.3237 11.6953 14.6811 10.8326C15.0385 9.96978 15.2224 9.04507 15.2224 8.11121C15.2224 7.17735 15.0385 6.25264 14.6811 5.38987C14.3237 4.5271 13.7999 3.74316 13.1396 3.08283C12.4793 2.42249 11.6953 1.89868 10.8326 1.54131C9.96978 1.18394 9.04507 1 8.11121 1C7.17735 1 6.25264 1.18394 5.38987 1.54131C4.5271 1.89868 3.74316 2.42249 3.08283 3.08283C1.74921 4.41644 1 6.2252 1 8.11121C1 9.99722 1.74921 11.806 3.08283 13.1396C4.41644 14.4732 6.2252 15.2224 8.11121 15.2224C9.99722 15.2224 11.806 14.4732 13.1396 13.1396Z"
                          stroke="#05F273"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <h2 className="font-semibold text-xl md:text-2xl text-white leading-[1.2]">
                      Project specifications
                    </h2>
                  </div>

                  {/* Description */}
                  <p className="text-sm text-white leading-[1.5]">
                    Fill the inputs with your specific project data to generate
                    an ROI estimation.
                  </p>

                  {/* Inputs */}
                  <div className="flex flex-col gap-8 w-full">
                    {/* Sliders */}
                    <div className="flex flex-col gap-6 w-full">
                      {calculator_inputs && calculator_inputs.length > 0 ? (
                        calculator_inputs
                          .filter((input) => input.input_type === 'slider')
                          .map((input, index) => {
                            // Map index to the correct slider ID if input_id is not set
                            const defaultIds = ['1', '2', '3'];
                            const inputId =
                              input.input_id || defaultIds[index] || '1';
                            const config =
                              SLIDER_CONFIG[inputId] || SLIDER_CONFIG['1'];
                            const currentValue =
                              sliderValues[inputId] ?? config.min;
                            return (
                              <SliderInput
                                key={`slider-${index}`}
                                label={input.label || ''}
                                helperText={input.helper_text || undefined}
                                inputId={inputId}
                                value={currentValue}
                                onChange={(value) =>
                                  updateSliderValue(inputId, value)
                                }
                              />
                            );
                          })
                      ) : (
                        // Fallback: render default sliders if no Prismic inputs
                        <>
                          <SliderInput
                            label="Source lines of code (SLO)"
                            helperText="Typical: 2K - 2M+ lines"
                            inputId="1"
                            value={sliderValues['1'] ?? SLIDER_CONFIG['1'].min}
                            onChange={(value) => updateSliderValue('1', value)}
                          />
                          <SliderInput
                            label="Repository Size (MB)"
                            helperText="Typical: 1 - 1500+ MB"
                            inputId="2"
                            value={sliderValues['2'] ?? SLIDER_CONFIG['2'].min}
                            onChange={(value) => updateSliderValue('2', value)}
                          />
                          <SliderInput
                            label="Internal Team Size"
                            helperText="Typical: 20 - 500+"
                            inputId="3"
                            value={sliderValues['3'] ?? SLIDER_CONFIG['3'].min}
                            onChange={(value) => updateSliderValue('3', value)}
                          />
                        </>
                      )}
                    </div>

                    {/* Checkboxes */}
                    {project_factors && project_factors.length > 0 && (
                      <div className="flex flex-col gap-2 w-full">
                        <p className="text-sm text-white leading-[1.5]">
                          Select the factors that apply to your project
                          challenges:
                        </p>
                        <div className="flex flex-nowrap justify-between items-center w-full">
                          {project_factors.map((factor, index) => (
                            <Checkbox
                              key={`factor-${index}`}
                              label={factor.factor_label || ''}
                              checked={
                                checkedFactors[`factor_${index}`] || false
                              }
                              onChange={(checked) =>
                                updateCheckedFactor(`factor_${index}`, checked)
                              }
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Calculate button */}
                  <button
                    type="button"
                    onClick={handleCalculateROI}
                    className="w-full bg-white/30 border-t border-white/80 rounded-full px-6 py-3 font-bold text-white text-center hover:bg-white/40 transition-colors"
                  >
                    {calculator_cta?.text || 'Calculate my ROI'}
                  </button>
                </div>
              )}
            </div>

            {/* Powered by badge */}
            <div className="bg-black/40 rounded-full px-3 py-1">
              <p className="text-sm text-center">
                <span className="text-white">Powered by </span>
                <span className="text-white font-medium">Velx</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AiModernizationRoiCalculator;
